import moment from "moment";
/**
 * Convierte un número de segundos a un formato de tiempo hh:mm:ss.
 *
 * @param {number} seconds - El número total de segundos a convertir.
 * @returns {string} - El tiempo formateado en el formato hh:mm:ss.
 *
 * @example
 * // Devuelve '00:02:15'
 * formatTime(135);
 *
 * @example
 * // Devuelve '01:00:00'
 * formatTime(3600);
 *
 * @example
 * // Devuelve '12:34:56'
 * formatTime(45296);
 */
export function formatTime(seconds) {
  // Calcular horas, minutos y segundos
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  // Formatear cada componente para tener dos dígitos
  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(secs).padStart(2, "0");

  // Combinar en formato hh:mm:ss
  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Convierte un tiempo en formato hh:mm:ss a un número total de segundos.
 *
 * @param {string} time - El tiempo en formato hh:mm:ss.
 * @returns {number} - El número total de segundos.
 *
 * @example
 * // Devuelve 135
 * parseTime("00:02:15");
 *
 * @example
 * // Devuelve 3600
 * parseTime("01:00:00");
 *
 * @example
 * // Devuelve 45296
 * parseTime("12:34:56");
 */
export function parseTime(time) {
  // Separar las horas, minutos y segundos
  const [hours, minutes, seconds] = time.split(":").map(Number);

  // Convertir todo a segundos y sumar
  return hours * 3600 + minutes * 60 + seconds;
}

export function formatTimeInt(intTime) {
  const paddedTime = `${intTime}`.padStart(6, "0");
  const hours = paddedTime.slice(0, 2);
  const minutes = paddedTime.slice(2, 4);
  const seconds = paddedTime.slice(4, 6);
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Devuelve la fecha de ayer.
 *
 * @returns {string} - La fecha de ayer en formato 'YYYY-MM-DD'.
 *
 * @example
 * getYesterday();
 */
export const getYesterday = () => moment().subtract(1, "days").toDate();

/**
 * Diferencia entre dos fechas.
 *
 * @param {Date} date1 - La fecha inicial.
 * @param {Date} date2 - La fecha final.
 * @returns {Array<number>} - Un arreglo de semanas, días, horas, minutos y segundos restantes.
 *
 * @example
 */
export const countDiff = (date1, date2) => {
  // Calcula la diferencia en milisegundos
  const diff = date2 - date1;

  if (diff <= 0) {
    // La fecha objetivo ya ha pasado
    return [0, 0, 0, 0, 0];
  }

  // Calcula semanas, días, horas, minutos y segundos restantes
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(diff / (1000 * 60 * 60 * 24 * 7));

  return [weeks, days, hours, minutes, seconds];
};

/**
 * Formato de Fecha YYYY-MM-DD.
 *
 * @param {Date} date - La fecha a formatear.
 * @returns {String} - La fecha en formato 'YYYY-MM-DD'.
 */
export const formatYYYMMDD = (date) => moment(date).format("YYYY-MM-DD");

/**
 * Formato de Fecha DD/MM/YYY.
 *
 * @param {Date} date - La fecha a formatear.
 * @returns {String} - La fecha en formato 'DD/MM/YYY'.
 */
export const formatDDMMYYY = (date) => moment(date).format("DD/MM/YYYY");

/**
 * Fecha de hoy en Chile. En UTC el día cambia a las 21:00 o 20:00 de Chile,
 * así que no sirve new Date().toISOString().
 *
 * @returns {String} - La fecha en formato 'YYYY-MM-DD'.
 */
export const hoyChile = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Santiago" }).format(new Date());

/**
 * Formato de Hora HH:mm.
 *
 * @param {Date} date - La fecha a formatear.
 * @returns {String} - La fecha en formato 'HH:mm'.
*/
export const formatHHmm = (date) => moment(date).format('HH:mm');

/**
 * Pasa de 0 - 2359 a formato HH:mm:SS
*/
export const hourIdToHourString = (id, ss = '00') => {
  const hours = String(Math.floor(id / 100)).padStart(2, '0');
  const minutes = String(id % 100).padStart(2, '0');
  return `${hours}:${minutes}:${ss}`;
};

/**
 * Pasa de formato HH:mm:SS a 0 - 2359
*/
export const hourStringToHourId = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return (hours * 100) + minutes;
};

export const meses = [
  { text: "Enero", value: 1, position: 0 },
  { text: "Febrero", value: 2, position: 1 },
  { text: "Marzo", value: 3, position: 2 },
  { text: "Abril", value: 4, position: 3 },
  { text: "Mayo", value: 5, position: 4 },
  { text: "Junio", value: 6, position: 5 },
  { text: "Julio", value: 7, position: 6 },
  { text: "Agosto", value: 8, position: 7 },
  { text: "Septiembre", value: 9, position: 8 },
  { text: "Octubre", value: 10, position: 9 },
  { text: "Noviembre", value: 11, position: 10 },
  { text: "Diciembre", value: 12, position: 11 },
];


/**
 * Genera un arreglo de años desde una fecha dada hasta otra.
 *
 * @param {object} args - Objeto con las fechas de inicio y fin.
 * @param {Date} args.desde - Fecha de inicio (por defecto es el 1 de enero del año actual).
 * @param {Date} args.hasta - Fecha de fin (por defecto es el año actual).
 * @returns {Array} - Un arreglo de años en formato 'YYYY'.
 */
export const agnos = (args) => {
  const { desde, hasta } = { hasta: new Date().getFullYear(), ...args };
  const years = [];

  for (let year = hasta; year >= desde.getFullYear(); year--) {
    years.push({ text: year.toString(), value: year, position: years.length });
  }
  return years;
};

/**
 * Convierte una fecha "de día" (DATEONLY del back, string ISO, timestamp o
 * Date) en un Date local a las 00:00, para pasársela a v-calendar.
 *
 * v-calendar solo interpreta los strings que calzan con su máscara ISO; con
 * cualquier otro string no vacío en min-date/max-date/v-model lanza
 * "Cannot read properties of null (reading 'getTime')" y no dibuja el mes.
 *
 * @param {string|number|Date} valor - La fecha a convertir.
 * @returns {Date|string} - El Date, o "" si no hay fecha o no es válida
 * (string vacío, como el valor inicial de los pickers).
 *
 * @example
 * // Devuelve el 6 de octubre de 2025 a las 00:00 (hora local)
 * aFechaPicker("2025-10-06");
 * aFechaPicker("2025-10-06T00:00:00");
 */
export function aFechaPicker(valor) {
  if (valor === null || valor === undefined || valor === "") return "";
  if (valor instanceof Date) {
    return isNaN(valor)
      ? ""
      : new Date(valor.getFullYear(), valor.getMonth(), valor.getDate());
  }
  const texto = String(valor);
  // Empieza con el día: se toma tal cual, sin pasar por UTC (new Date("2025-10-06")
  // es la medianoche UTC, que en Chile todavía es el día anterior)
  const dia = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dia) return new Date(+dia[1], +dia[2] - 1, +dia[3]);
  const fecha = /^\d+$/.test(texto) ? new Date(+texto) : new Date(texto);
  if (isNaN(fecha)) return "";
  // Un timestamp de una fecha sin hora es la medianoche UTC: se usa el día UTC
  return new Date(fecha.getUTCFullYear(), fecha.getUTCMonth(), fecha.getUTCDate());
}

/**
 * Arma "YYYY-MM-DDTHH:mm:ss" con el día local de una fecha y la hora indicada,
 * sin zona horaria: el día se manda tal como se eligió en el calendario (un
 * Date se serializa en UTC y las 23:59 de Chile ya son el día siguiente).
 *
 * @param {string|number|Date} valor - La fecha (cualquier formato de aFechaPicker).
 * @param {string} hora - La hora en formato 'HH:mm:ss'.
 * @returns {string} - La fecha con la hora, o "" si no hay fecha.
 *
 * @example
 * // Devuelve '2026-10-30T23:59:59'
 * fechaConHora(new Date(2026, 9, 30), "23:59:59");
 */
export function fechaConHora(valor, hora) {
  const fecha = aFechaPicker(valor);
  if (!fecha) return "";
  const dosDigitos = (numero) => String(numero).padStart(2, "0");
  const dia = `${fecha.getFullYear()}-${dosDigitos(fecha.getMonth() + 1)}-${dosDigitos(fecha.getDate())}`;
  return `${dia}T${hora}`;
}
